import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert
} from '@mui/material';
import DatabaseService from '../../services/db';
import PropTypes from 'prop-types';

// Average portion sizes for reference
const AVERAGE_PORTION_SIZES = {
  g: 30,
  kg: 0.15,
  ml: 250,
  L: 0.25,
  item: 1,
  pack: 1,
  can: 1,
  box: 1,
  bottle: 1,
  carton: 1,
  jar: 1
};

export default function WasteDialog({
  open,
  item,
  onClose,
  onWasteLogged,
  onConsumed,
  currentUser,
  mode = 'waste',
}) {
  const isConsume = mode === 'consume';
  const [quantity, setQuantity] = useState(1);
  const [portions, setPortions] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (item) {
      const currentQty = Number(item.quantity) || 1;
      setQuantity(Math.max(1, Math.min(currentQty, Math.floor(currentQty / 2) || 1)));
      if (item.unit !== 'item' && AVERAGE_PORTION_SIZES[item.unit]) {
        setPortions(Math.max(1, Math.round((currentQty / AVERAGE_PORTION_SIZES[item.unit]) || 1)));
      } else {
        setPortions(1);
      }
      setError('');
      setSuccess('');
    }
  }, [item, mode, open]);

  const handleSubmit = async () => {
    if (!item || !currentUser) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const currentQty = Number(item.quantity) || 0;
      const usedQty = Number(quantity) || 0;
      const newQuantity = currentQty - usedQty;
      if (newQuantity <= 0) {
        await DatabaseService.items.delete(item.id);
      } else {
        await DatabaseService.items.update(item.id, {
          ...item,
          quantity: newQuantity
        });
      }

      const baseLog = {
        userId: currentUser.uid,
        itemId: item.id,
        itemName: item.name,
        quantity: usedQty,
        unit: item.unit,
        date: new Date(),
        category: item.category || 'Uncategorized',
        location: item.location || 'Unknown',
      };

      if (item.price !== undefined && item.price !== null && item.price !== '') {
        const price = Number(item.price);
        if (Number.isFinite(price) && price >= 0) {
          baseLog.price = price;
        }
      }

      if (item.unit !== 'item' && portions !== undefined && portions !== null) {
        baseLog.portions = portions;
      }

      if (isConsume) {
        await DatabaseService.consumption.add({
          ...baseLog,
          type: 'consume',
        });
        setSuccess('Marked as consumed.');
        if (onConsumed) onConsumed();
        else if (onWasteLogged) onWasteLogged();
      } else {
        await DatabaseService.wasteLogs.add(baseLog);
        setSuccess('Waste logged successfully!');
        if (onWasteLogged) onWasteLogged();
      }

      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 1000);
    } catch (err) {
      console.error(isConsume ? 'Error logging consumption:' : 'Error logging waste:', err);
      setError(isConsume ? 'Failed to mark as consumed.' : 'Failed to log waste.');
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  const maxQuantity = Number(item.quantity) || 1;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isConsume ? 'Mark as Consumed' : 'Report Waste'}</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          {item.name} - Current quantity: {item.quantity} {item.unit}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isConsume
            ? 'Use this when you successfully used the food. It will leave your inventory and count as consumed, not waste.'
            : 'Use this when food was thrown away or spoiled.'}
        </Typography>
        <TextField
          fullWidth
          label="Quantity"
          type="number"
          value={quantity}
          onChange={e => {
            let val = Number(e.target.value);
            if (val < 1) val = 1;
            if (val > maxQuantity) val = maxQuantity;
            setQuantity(val);
          }}
          inputProps={{ min: 1, max: maxQuantity, step: 1 }}
          helperText={`Enter amount to ${isConsume ? 'consume' : 'waste'} (max: ${maxQuantity} ${item.unit})`}
          sx={{ mb: 2 }}
        />
        {item.unit !== 'item' && AVERAGE_PORTION_SIZES[item.unit] && (
          <TextField
            fullWidth
            label="Portions"
            type="number"
            value={portions}
            onChange={e => setPortions(Math.max(1, Math.round(Number(e.target.value))))}
            inputProps={{ min: 1, step: 1 }}
            sx={{ mb: 2 }}
            helperText={`A typical portion for ${item.unit} is ${AVERAGE_PORTION_SIZES[item.unit]}${item.unit}.`}
          />
        )}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color={isConsume ? 'success' : 'warning'}
          variant="contained"
          disabled={loading || quantity < 1 || quantity > maxQuantity}
        >
          {isConsume ? 'Mark Consumed' : 'Log Waste'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

WasteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    id: PropTypes.any,
    name: PropTypes.string,
    quantity: PropTypes.number,
    unit: PropTypes.string,
    category: PropTypes.string,
    location: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onClose: PropTypes.func.isRequired,
  onWasteLogged: PropTypes.func,
  onConsumed: PropTypes.func,
  currentUser: PropTypes.object,
  mode: PropTypes.oneOf(['waste', 'consume']),
};
